/**
 * The session query. One definition, used by RequireAuth, ProfileProvider and
 * anything that needs the signed-in user.
 *
 * `GET /api/auth/session` returns `{ user, profiles }` (shared `SessionResponse`).
 * A 401 here is not an error to display, it is "not signed in" — so this query
 * never retries and never refetches on focus. Retrying a 401 just delays the
 * redirect to /login by a couple of seconds.
 */

import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type { Profile, User } from '@medtrack/shared';

import { api, ApiClientError } from './api';
import { qk } from './queryKeys';

export interface SessionPayload {
  user: User;
  /** Every profile this user can see, each carrying the user's role on it. */
  profiles: Profile[];
}

export function fetchSession(signal?: AbortSignal): Promise<SessionPayload> {
  return api.get<SessionPayload>('/auth/session', undefined, { signal });
}

export function useSession(): UseQueryResult<SessionPayload, ApiClientError> {
  return useQuery<SessionPayload, ApiClientError>({
    queryKey: qk.session(),
    queryFn: ({ signal }) => fetchSession(signal),
    // A 401 is an answer, not a failure. Retrying it is pure latency.
    retry: false,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    gcTime: Infinity,
  });
}

/** True when the failure means "no session", as opposed to "server is down". */
export function isUnauthorized(error: unknown): boolean {
  return error instanceof ApiClientError && error.status === 401;
}

/**
 * Seed the session cache straight from a login/signup response so the app does
 * not immediately re-fetch what the server just told us.
 */
export function useSeedSession(): (payload: SessionPayload) => void {
  const queryClient = useQueryClient();
  return (payload: SessionPayload) => {
    queryClient.setQueryData(qk.session(), payload);
    queryClient.setQueryData(qk.profiles(), payload.profiles);
  };
}
